import React, { useState, useEffect } from 'react';
import './SystemMonitor.css';

// Use absolute URLs based on current host to ensure proxy works from any IP
// The proxy (setupProxy.js) will forward /api requests to backend
function getApiBase() {
  if (import.meta.env.REACT_APP_API_URL) {
    return import.meta.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined') {
    // Use window.location.origin which includes protocol, hostname, and port
    return window.location.origin;
  }
  return '';
}
const API_BASE = getApiBase();

/** Display label only. The governor still loads the full Ollama tag. */
function shortModelName(name) {
  const raw = String(name || '').trim();
  const lower = raw.toLowerCase();
  if (lower.includes('optilaw-librarian')) return 'Librarian';
  const bare = raw.replace(/:latest$/i, '');
  const sized = bare.match(/^([a-z0-9.]+):(\d+(?:\.\d+)?b)\b/i);
  if (sized) return `${sized[1]}:${sized[2].toLowerCase()}`;
  return bare;
}

function adminSessionHeaders() {
  try {
    const t = localStorage.getItem('sessionToken');
    return t ? { 'X-Admin-Session': t } : {};
  } catch {
    return {};
  }
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...adminSessionHeaders(), ...(options.headers || {}) },
  });
  return res;
}

const SystemMonitor = () => {
  const [serverStats, setServerStats] = useState({
    cpu: { usage: 0, cores: 0, model: '' },
    memory: { used: 0, total: 0, percentage: 0, available: 0 },
    disk: { used: 0, total: 0, percentage: 0 },
    network: { rx: 0, tx: 0 },
    uptime: 0,
    load: [0, 0, 0],
    serverTime: {
      timestamp: '',
      localTime: '',
      timezone: '',
      unixTimestamp: 0,
      dayOfYear: 0,
      weekOfYear: 0
    },
    platform: {
      os: '',
      release: '',
      arch: '',
      hostname: ''
    },
    gpu: null,
    gpuGovernor: null,
  });
  const [hetznerStats, setHetznerStats] = useState({
    status: 'unknown',
    location: 'Hetzner Cloud',
    ip: 'Loading...',
    bandwidth: { in: 0, out: 0 }
  });
  const [pm2Services, setPm2Services] = useState({
    summary: { total: 0, online: 0, scheduled: 0, stopped: 0, errored: 0, flapping: 0, totalMemory: 0, totalRestarts: 0, totalRestartsPerMin: 0 },
    services: [],
    servicesByCategory: {}
  });
  const [standaloneServices, setStandaloneServices] = useState({
    summary: { total: 0, online: 0, offline: 0, unknown: 0 },
    services: [],
    servicesByCategory: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Fetch system stats from our server
  const fetchSystemStats = async () => {
    try {
      const response = await apiFetch('/api/system/stats');
      if (response.status === 401) {
        setError('Session expired — please log out and sign in again.');
        return;
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch system stats (${response.status})`);
      }
      const data = await response.json();
      setServerStats(data);
      setError('');
    } catch (err) {
      console.error('Error fetching system stats:', err);
      setError('Failed to load system statistics');
    }
  };

  // Fetch server info (formerly Hetzner stats, now Spark machine stats)
  const fetchHetznerStats = async () => {
    try {
      const response = await apiFetch('/api/system/hetzner');
      if (!response.ok) {
        throw new Error('Failed to fetch server stats');
      }
      const data = await response.json();
      setHetznerStats(data);
    } catch (err) {
      console.error('Error fetching server stats:', err);
      // Set fallback data
      setHetznerStats({
        status: 'running',
        location: 'Spark Development Machine - Local Network',
        ip: 'Not available',
        bandwidth: { in: 0, out: 0 }
      });
    }
  };

  // Fetch PM2 services info
  const fetchPm2Services = async () => {
    try {
      const response = await apiFetch('/api/system/pm2-services');
      if (!response.ok) {
        throw new Error('Failed to fetch PM2 services');
      }
      const data = await response.json();
      setPm2Services(data);
    } catch (err) {
      console.error('Error fetching PM2 services:', err);
      // Set fallback data
      setPm2Services({
        summary: { total: 0, online: 0, scheduled: 0, stopped: 0, errored: 0, flapping: 0, totalMemory: 0, totalRestarts: 0, totalRestartsPerMin: 0 },
        services: [],
        servicesByCategory: {}
      });
    }
  };

  // Fetch standalone services info
  const fetchStandaloneServices = async () => {
    try {
      const response = await apiFetch('/api/system/standalone-services');
      if (!response.ok) {
        throw new Error('Failed to fetch standalone services');
      }
      const data = await response.json();
      setStandaloneServices(data);
    } catch (err) {
      console.error('Error fetching standalone services:', err);
      // Set fallback data
      setStandaloneServices({
        summary: { total: 0, online: 0, offline: 0, unknown: 0 },
        services: [],
        servicesByCategory: {}
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSystemStats(), fetchHetznerStats(), fetchPm2Services(), fetchStandaloneServices()]);
      setLoading(false);
      setLastUpdate(new Date());
    };

    loadData();

    // Refresh every 10 seconds so flap detection surfaces quickly
    const interval = setInterval(() => {
      fetchSystemStats();
      fetchHetznerStats();
      fetchPm2Services();
      fetchStandaloneServices();
      setLastUpdate(new Date());
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Mutate browser tab title when something is flapping so a human monitoring
  // the dashboard in a background tab still notices. Restore on unmount.
  useEffect(() => {
    const flapping = pm2Services.summary?.flapping || 0;
    const originalTitle = 'OptiPrime Admin';
    if (flapping > 0) {
      document.title = `🚨 ${flapping} FLAPPING — ${originalTitle}`;
    } else {
      document.title = originalTitle;
    }
    return () => {
      document.title = originalTitle;
    };
  }, [pm2Services.summary?.flapping]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getStatusColor = (percentage) => {
    if (percentage < 50) return '#4CAF50'; // Green
    if (percentage < 80) return '#FF9800'; // Orange
    return '#F44336'; // Red
  };

  const getServiceStatusColor = (status) => {
    switch (status) {
      case 'online': return '#4CAF50'; // Green
      case 'flapping': return '#E91E63'; // Magenta/pink — rapid restart loop
      case 'scheduled': return '#00ACC1'; // Cyan — PM2 cron, idle between runs
      case 'stopped': return '#9E9E9E'; // Gray
      case 'offline': return '#9E9E9E'; // Gray
      case 'errored': return '#F44336'; // Red
      case 'stopping': return '#FF9800'; // Orange
      case 'launching': return '#2196F3'; // Blue
      case 'unknown': return '#FF9800'; // Orange
      default: return '#9E9E9E'; // Gray
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Core': return 'fa-heart';
      case 'Frontend': return 'fa-desktop';
      case 'Platform': return 'fa-layer-group';
      case 'Admin': return 'fa-user-shield';
      case 'AI': return 'fa-robot';
      case 'Infrastructure': return 'fa-server';
      case 'Development': return 'fa-code';
      default: return 'fa-cog';
    }
  };



  if (loading) {
    return (
      <div className="system-monitor">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading system statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="system-monitor">
      <div className="monitor-header">
        <div className="last-update">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {pm2Services.summary?.flapping > 0 && (
        <div
          role="alert"
          style={{
            background: 'linear-gradient(90deg, #B71C1C, #E53935)',
            color: '#fff',
            padding: '14px 18px',
            borderRadius: 6,
            marginBottom: 16,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(183,28,28,0.35)',
            animation: 'pulse 1.5s infinite'
          }}
        >
          <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
          {pm2Services.summary.flapping} service
          {pm2Services.summary.flapping > 1 ? 's are' : ' is'} FLAPPING
          (crash-restart loop detected):{' '}
          {pm2Services.services
            .filter((s) => s.isFlapping)
            .map((s) => `${s.displayName || s.name} (+${s.restartsPerMin}/min)`)
            .join(', ')}
          . Check PM2 logs and port conflicts.
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.75; }
          100% { opacity: 1; }
        }
      `}</style>

      <div className="stats-grid">
        {/* Server Status Card */}
        <div className="stat-card server-status">
          <div className="card-header">
            <h3><i className="fas fa-cloud-server"></i> Server Status</h3>
            <span className={`status-badge ${hetznerStats.status}`}>
              {hetznerStats.status.toUpperCase()}
            </span>
          </div>
          <div className="card-content">
            <div className="info-row">
              <span className="label">Location:</span>
              <span className="value">{hetznerStats.location}</span>
            </div>
            <div className="info-row">
              <span className="label">IP Address:</span>
              <span className="value">{hetznerStats.ip}</span>
            </div>
            <div className="info-row">
              <span className="label">Hostname:</span>
              <span className="value">{serverStats.platform.hostname}</span>
            </div>
            <div className="info-row">
              <span className="label">Uptime:</span>
              <span className="value">{formatUptime(serverStats.uptime)}</span>
            </div>
          </div>
        </div>

        {/* Server Time Card */}
        <div className="stat-card">
          <div className="card-header">
            <h3><i className="fas fa-clock"></i> Server Time</h3>
          </div>
          <div className="card-content">
            <div className="info-row">
              <span className="label">Current Time:</span>
              <span className="value">{serverStats.serverTime.localTime}</span>
            </div>
            <div className="info-row">
              <span className="label">Timezone:</span>
              <span className="value">{serverStats.serverTime.timezone}</span>
            </div>
            <div className="info-row">
              <span className="label">Day of Year:</span>
              <span className="value">Day {serverStats.serverTime.dayOfYear}</span>
            </div>
            <div className="info-row">
              <span className="label">Week of Year:</span>
              <span className="value">Week {serverStats.serverTime.weekOfYear}</span>
            </div>
            <div className="info-row">
              <span className="label">Unix Timestamp:</span>
              <span className="value">{serverStats.serverTime.unixTimestamp}</span>
            </div>
          </div>
        </div>

        {/* CPU Usage Card */}
        <div className="stat-card">
          <div className="card-header">
            <h3><i className="fas fa-microchip"></i> CPU Usage</h3>
            <span className="percentage">{serverStats.cpu.usage.toFixed(1)}%</span>
          </div>
          <div className="card-content">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${serverStats.cpu.usage}%`,
                  backgroundColor: getStatusColor(serverStats.cpu.usage)
                }}
              ></div>
            </div>
            <div className="info-row">
              <span className="label">Cores:</span>
              <span className="value">{serverStats.cpu.cores}</span>
            </div>
            <div className="info-row">
              <span className="label">Model:</span>
              <span className="value">{serverStats.cpu.model.substring(0, 30)}...</span>
            </div>
            <div className="info-row">
              <span className="label">Load Average:</span>
              <span className="value">
                {serverStats.load.map(l => l.toFixed(2)).join(', ')}
              </span>
            </div>
          </div>
        </div>

        {/* Memory Usage Card */}
        <div className="stat-card">
          <div className="card-header">
            <h3><i className="fas fa-memory"></i> Memory Usage</h3>
            <span className="percentage">{serverStats.memory.percentage.toFixed(1)}%</span>
          </div>
          <div className="card-content">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${serverStats.memory.percentage}%`,
                  backgroundColor: getStatusColor(serverStats.memory.percentage)
                }}
              ></div>
            </div>
            <div className="info-row">
              <span className="label">Used:</span>
              <span className="value">{formatBytes(serverStats.memory.used)}</span>
            </div>
            <div className="info-row">
              <span className="label">Total:</span>
              <span className="value">{formatBytes(serverStats.memory.total)}</span>
            </div>
          </div>
        </div>

        {/* Disk Usage Card */}
        <div className="stat-card">
          <div className="card-header">
            <h3><i className="fas fa-hdd"></i> Disk Usage</h3>
            <span className="percentage">{serverStats.disk.percentage.toFixed(1)}%</span>
          </div>
          <div className="card-content">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${serverStats.disk.percentage}%`,
                  backgroundColor: getStatusColor(serverStats.disk.percentage)
                }}
              ></div>
            </div>
            <div className="info-row">
              <span className="label">Used:</span>
              <span className="value">{formatBytes(serverStats.disk.used)}</span>
            </div>
            <div className="info-row">
              <span className="label">Total:</span>
              <span className="value">{formatBytes(serverStats.disk.total)}</span>
            </div>
          </div>
        </div>

        {/* GPU Card - Only show if GPU is available */}
        {serverStats.gpu && (
          <div className="stat-card">
            <div className="card-header">
              <h3><i className="fas fa-microchip"></i> GPU (NVIDIA)</h3>
              <span className="percentage">{serverStats.gpu.utilization}%</span>
            </div>
            <div className="card-content">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${serverStats.gpu.utilization}%`,
                    backgroundColor: getStatusColor(serverStats.gpu.utilization)
                  }}
                ></div>
              </div>
              <div className="info-row">
                <span className="label">Model:</span>
                <span className="value">{serverStats.gpu.name}</span>
              </div>
              {serverStats.gpu.memoryTotal && serverStats.gpu.memoryUsed !== null && (
                <>
                  <div className="info-row">
                    <span className="label">Memory Used:</span>
                    <span className="value">{serverStats.gpu.memoryUsed} MB / {serverStats.gpu.memoryTotal} MB</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Memory Usage:</span>
                    <span className="value">{serverStats.gpu.memoryPercentage}%</span>
                  </div>
                </>
              )}
              <div className="info-row">
                <span className="label">Temperature:</span>
                <span className="value">
                  {serverStats.gpu.temperature != null ? `${serverStats.gpu.temperature}°C` : '—'}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Utilization:</span>
                <span className="value">{serverStats.gpu.utilization}%</span>
              </div>
            </div>
          </div>
        )}

        {/* GPU Governor Card */}
        {serverStats.gpuGovernor && (
          <div className="stat-card">
            <div className="card-header">
              <h3><i className="fas fa-shield-alt"></i> GPU Governor</h3>
              <span className={`status-badge ${serverStats.gpuGovernor.online ? 'status-online' : 'status-error'}`}>
                {serverStats.gpuGovernor.online ? 'Online' : 'Offline'}
              </span>
            </div>
            <div className="card-content">
              {serverStats.gpuGovernor.online && serverStats.gpuGovernor.memory && (
                <>
                  <div className="info-row">
                    <span className="label">Memory Used:</span>
                    <span className="value" style={{ color: serverStats.gpuGovernor.memory.usedPct > 85 ? '#ef4444' : serverStats.gpuGovernor.memory.usedPct > 70 ? '#f59e0b' : '#22c55e' }}>
                      {serverStats.gpuGovernor.memory.usedPct}% ({serverStats.gpuGovernor.memory.availableGB} GB free)
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">Ceiling:</span>
                    <span className="value">{serverStats.gpuGovernor.memoryCeilingPct}%</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Progress:</span>
                    <span className="value" style={{ flex: 1 }}>
                      <div style={{ background: '#1e293b', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                        <div style={{
                          width: `${Math.min(100, serverStats.gpuGovernor.memory.usedPct || 0)}%`,
                          height: '100%',
                          background: serverStats.gpuGovernor.memory.usedPct > 85 ? '#ef4444' : serverStats.gpuGovernor.memory.usedPct > 70 ? '#f59e0b' : '#22c55e',
                          transition: 'width 0.3s',
                        }} />
                      </div>
                    </span>
                  </div>
                </>
              )}
              {serverStats.gpuGovernor.online && serverStats.gpuGovernor.imageModel && (
                <div className="info-row">
                  <span className="label">Image Model:</span>
                  <span className="value" style={{ color: ['parking-chat', 'restoring-chat', 'video', 'guest'].includes(serverStats.gpuGovernor.imageSwap?.phase) || serverStats.gpuGovernor.imageModel.loading ? '#f59e0b' : undefined }}>
                    {serverStats.gpuGovernor.imageSwap?.phase === 'parking-chat'
                      ? 'Parking the pinned models'
                      : serverStats.gpuGovernor.imageSwap?.phase === 'restoring-chat'
                        ? 'Reloading the pinned models'
                        : serverStats.gpuGovernor.imageSwap?.phase === 'video'
                          ? 'Video model has the GPU'
                          : serverStats.gpuGovernor.imageSwap?.phase === 'guest'
                            ? 'Another model has the GPU'
                        : serverStats.gpuGovernor.imageModel.loading
                          ? 'Loading'
                          : serverStats.gpuGovernor.imageModel.loaded
                            ? `Loaded — idle ${Math.round(serverStats.gpuGovernor.imageModel.idleSeconds || 0)}s / ${serverStats.gpuGovernor.imageModel.idleTimeoutSec || 300}s`
                            : 'Unloaded'}
                  </span>
                </div>
              )}
              {serverStats.gpuGovernor.online && serverStats.gpuGovernor.ollamaLoadedModels && serverStats.gpuGovernor.ollamaLoadedModels.length > 0 && (
                <div className="info-row">
                  <span className="label">LLM Loaded:</span>
                  <span
                    className="value model-names"
                    title={serverStats.gpuGovernor.ollamaLoadedModels.map((m) => m.name).join(', ')}
                  >
                    {serverStats.gpuGovernor.ollamaLoadedModels.map((m) => shortModelName(m.name)).join(', ')}
                  </span>
                </div>
              )}
              {serverStats.gpuGovernor.online && serverStats.gpuGovernor.queueLength > 0 && (
                <div className="info-row">
                  <span className="label">Queue:</span>
                  <span className="value" style={{ color: '#f59e0b' }}>{serverStats.gpuGovernor.queueLength} waiting</span>
                </div>
              )}
              {serverStats.gpuGovernor.online && serverStats.gpuGovernor.deferred && serverStats.gpuGovernor.deferred.length > 0 && (
                <div className="info-row">
                  <span className="label">Deferred:</span>
                  <span className="value" style={{ color: '#ef4444' }}>{serverStats.gpuGovernor.deferred.length} LLM call(s) held</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Network Activity Card */}
        <div className="stat-card">
          <div className="card-header">
            <h3><i className="fas fa-network-wired"></i> Network Activity</h3>
          </div>
          <div className="card-content">
            <div className="info-row">
              <span className="label">
                <i className="fas fa-download"></i> Received:
              </span>
              <span className="value">{formatBytes(serverStats.network.rx)}</span>
            </div>
            <div className="info-row">
              <span className="label">
                <i className="fas fa-upload"></i> Transmitted:
              </span>
              <span className="value">{formatBytes(serverStats.network.tx)}</span>
            </div>
            <div className="info-row">
              <span className="label">Bandwidth In:</span>
              <span className="value">{formatBytes(hetznerStats.bandwidth.in)}/s</span>
            </div>
            <div className="info-row">
              <span className="label">Bandwidth Out:</span>
              <span className="value">{formatBytes(hetznerStats.bandwidth.out)}/s</span>
            </div>
          </div>
        </div>

        {/* Platform Information Card */}
        <div className="stat-card">
          <div className="card-header">
            <h3><i className="fas fa-info-circle"></i> Platform Info</h3>
          </div>
          <div className="card-content">
            <div className="info-row">
              <span className="label">Operating System:</span>
              <span className="value">{serverStats.platform.os}</span>
            </div>
            <div className="info-row">
              <span className="label">Kernel Release:</span>
              <span className="value">{serverStats.platform.release}</span>
            </div>
            <div className="info-row">
              <span className="label">Architecture:</span>
              <span className="value">{serverStats.platform.arch}</span>
            </div>
            <div className="info-row">
              <span className="label">Available Memory:</span>
              <span className="value">{formatBytes(serverStats.memory.available)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* PM2 Services Section */}
      <div className="pm2-services-section">
        <div className="section-header">
          <h2><i className="fas fa-cogs"></i> PM2 Services Status</h2>
          <div className="services-summary">
            <span className="summary-item online">
              <i className="fas fa-circle"></i> {pm2Services.summary.online} Online
            </span>
            {(pm2Services.summary.scheduled > 0) && (
              <span className="summary-item" style={{ color: '#00ACC1' }}>
                <i className="fas fa-circle"></i> {pm2Services.summary.scheduled} Scheduled (cron)
              </span>
            )}
            <span className="summary-item stopped">
              <i className="fas fa-circle"></i> {pm2Services.summary.stopped} Stopped
            </span>
            <span className="summary-item errored">
              <i className="fas fa-circle"></i> {pm2Services.summary.errored} Errored
            </span>
            <span className="summary-item total">
              Total: {pm2Services.summary.total} | Memory: {pm2Services.summary.totalMemory}MB
            </span>
          </div>
        </div>

        {Object.entries(pm2Services.servicesByCategory).map(([category, services]) => (
          <div key={category} className="service-category">
            <h3 className="category-title">
              <i className={`fas ${getCategoryIcon(category)}`}></i>
              {category} Services ({services.length})
            </h3>
            <div className="services-grid">
              {services.map(service => (
                <div key={service.id} className="service-card">
                  <div className="service-header">
                    <div className="service-info">
                      <i className={service.icon}></i>
                      <div>
                        <h4>{service.displayName}</h4>
                        <p className="service-description">{service.description}</p>
                      </div>
                    </div>
                    <div className="service-status">
                      <span 
                        className={`status-indicator ${service.status}`}
                        style={{
                          backgroundColor: getServiceStatusColor(service.status),
                          animation: service.isFlapping ? 'pulse 1s infinite' : 'none'
                        }}
                      >
                        {service.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="service-details">
                    {service.isFlapping && (
                      <div
                        className="detail-row"
                        style={{
                          backgroundColor: '#FDECEA',
                          color: '#B71C1C',
                          padding: '6px 8px',
                          borderRadius: 4,
                          marginBottom: 6,
                          fontWeight: 600
                        }}
                        title="PM2 says the process is online but it is restarting rapidly. Check logs."
                      >
                        ⚠ FLAPPING: {service.restartsPerMin}/min
                        {service.unstableRestarts > 0 ? ` · unstable=${service.unstableRestarts}` : ''}
                        {service.exitCode != null ? ` · exit=${service.exitCode}` : ''}
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="label">Process ID:</span>
                      <span className="value">{service.pid || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Memory:</span>
                      <span className="value">{formatBytes(service.memory || 0)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">CPU:</span>
                      <span className="value">{service.cpu}%</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Restarts:</span>
                      <span
                        className="value"
                        style={service.restartsPerMin > 0 ? { color: '#B71C1C', fontWeight: 600 } : undefined}
                      >
                        {service.restarts}
                        {service.restartsPerMin > 0 && (
                          <span style={{ marginLeft: 6, fontSize: '0.85em' }}>
                            (+{service.restartsPerMin}/min)
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Uptime:</span>
                      <span className="value">
                        {(service.status === 'online' || service.status === 'flapping') && service.uptime != null
                          ? formatUptime(service.uptime)
                          : 'N/A'}
                      </span>
                    </div>
                    {service.scheduleNote && (
                      <div className="detail-row">
                        <span className="label">Schedule:</span>
                        <span className="value" style={{ fontSize: '0.85rem' }}>{service.scheduleNote}</span>
                      </div>
                    )}
                    {service.port && (
                      <div className="detail-row">
                        <span className="label">Port:</span>
                        <span className="value">{service.port}</span>
                      </div>
                    )}
                  </div>

                  <div className="service-urls">
                    {service.devUrl && (
                      <a 
                        href={service.devUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="url-link dev"
                      >
                        <i className="fas fa-external-link-alt"></i>
                        Development
                      </a>
                    )}
                    {service.prodUrl && (
                      <a 
                        href={service.prodUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="url-link prod"
                      >
                        <i className="fas fa-external-link-alt"></i>
                        Production
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Standalone Services Section */}
      <div className="pm2-services-section">
        <div className="section-header">
          <h2><i className="fas fa-tools"></i> Standalone Services Status</h2>
          <div className="services-summary">
            <span className="summary-item online">
              <i className="fas fa-circle"></i> {standaloneServices.summary.online} Online
            </span>
            <span className="summary-item stopped">
              <i className="fas fa-circle"></i> {standaloneServices.summary.offline} Offline
            </span>
            <span className="summary-item errored">
              <i className="fas fa-circle"></i> {standaloneServices.summary.unknown} Unknown
            </span>
            <span className="summary-item total">
              Total: {standaloneServices.summary.total} | Non-PM2 Services
            </span>
          </div>
        </div>

        {Object.entries(standaloneServices.servicesByCategory).map(([category, services]) => (
          <div key={category} className="service-category">
            <h3 className="category-title">
              <i className={`fas ${getCategoryIcon(category)}`}></i>
              {category} Services ({services.length})
            </h3>
            <div className="services-grid">
              {services.map(service => (
                <div key={service.id} className="service-card">
                  <div className="service-header">
                    <div className="service-info">
                      <i className={service.icon}></i>
                      <div>
                        <h4>{service.name}</h4>
                        <p className="service-description">{service.description}</p>
                        {service.devNote && (
                          <p className="service-dev-note" style={{ fontSize: '0.85rem', color: '#9E9E9E', marginTop: '4px' }}>
                            <i className="fas fa-info-circle"></i> {service.devNote}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="service-status">
                      {service.devRequired === false ? (
                        <span 
                          className="status-indicator dev-not-required"
                          style={{ backgroundColor: '#9E9E9E', fontSize: '0.75rem' }}
                          title={service.devNote || 'Not required for development'}
                        >
                          DEV: NOT REQUIRED
                        </span>
                      ) : (
                        <span 
                          className={`status-indicator ${service.status}`}
                          style={{ backgroundColor: getServiceStatusColor(service.status) }}
                        >
                          {service.status.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="service-details">
                    <div className="detail-row">
                      <span className="label">Process ID:</span>
                      <span className="value">{service.pid || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Type:</span>
                      <span className="value">{service.type}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Memory:</span>
                      <span className="value">{service.memory}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">CPU:</span>
                      <span className="value">{service.cpu}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Restarts:</span>
                      <span className="value">{service.restarts}</span>
                    </div>
                    {service.port && (
                      <div className="detail-row">
                        <span className="label">Port:</span>
                        <span className="value">{service.port}</span>
                      </div>
                    )}
                  </div>

                  <div className="service-urls">
                    {service.devUrl && (
                      <a 
                        href={service.devUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="url-link dev"
                      >
                        <i className="fas fa-external-link-alt"></i>
                        Development
                      </a>
                    )}
                    {service.prodUrl && (
                      <a 
                        href={service.prodUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="url-link prod"
                      >
                        <i className="fas fa-external-link-alt"></i>
                        Production
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="stats-grid">

        {/* Quick Actions Card */}
        <div className="stat-card actions-card">
          <div className="card-header">
            <h3><i className="fas fa-tools"></i> Quick Actions</h3>
          </div>
          <div className="card-content">
            <button className="action-btn" onClick={fetchSystemStats}>
              <i className="fas fa-sync-alt"></i> Refresh Stats
            </button>
            <button className="action-btn" onClick={() => window.location.href = '/dashboard'}>
              <i className="fas fa-tachometer-alt"></i> Main Dashboard
            </button>
            <button className="action-btn" onClick={() => window.open(`${API_BASE}/api/system/logs`, '_blank')}>
              <i className="fas fa-file-alt"></i> View Logs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitor;
